import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './BoutiqueSetupWizard.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BoutiqueSetupWizard = ({ onComplete, onCancel }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Form data
  const [formData, setFormData] = useState({
    name: 'My Style',
    modelAlbumId: '',
    modelPhotoId: '',
    brands: [],
    itemsPerBrand: 5,
    frequency: 'weekly',
    generateNow: true
  });

  // Data for dropdowns
  const [albums, setAlbums] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [brandSearch, setBrandSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  const sessionToken = localStorage.getItem('sessionToken');

  // Load albums
  useEffect(() => {
    loadAlbums();
  }, []);

  // Load photos when album selected
  useEffect(() => {
    if (formData.modelAlbumId) {
      loadPhotos(formData.modelAlbumId);
    }
  }, [formData.modelAlbumId]);

  const loadAlbums = async () => {
    try {
      const response = await axios.get(`${API}/albums?include_photos=false`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      if (response.data.success) {
        setAlbums(response.data.albums);
      }
    } catch (err) {
      console.error('Error loading albums:', err);
    }
  };

  const loadPhotos = async (albumId) => {
    try {
      const response = await axios.get(`${API}/albums?include_photos=true`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      if (response.data.success) {
        const album = response.data.albums.find(a => a.id === albumId);
        if (album) {
          setPhotos(album.photos || []);
        }
      }
    } catch (err) {
      console.error('Error loading photos:', err);
    }
  };

  const searchBrands = async () => {
    if (!brandSearch.trim()) return;
    
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/garments/search/live?query=${encodeURIComponent(brandSearch)}&num_results=10`,
        {
          headers: { Authorization: `Bearer ${sessionToken}` }
        }
      );
      
      if (response.data.success) {
        // Group by brand
        const brandMap = {};
        response.data.garments.forEach(item => {
          if (!brandMap[item.brand]) {
            brandMap[item.brand] = {
              name: item.brand,
              type: 'serpapi',
              id: `serpapi_${item.brand.toLowerCase().replace(/\s+/g, '_')}`
            };
          }
        });
        setSearchResults(Object.values(brandMap));
      }
    } catch (err) {
      console.error('Error searching brands:', err);
    } finally {
      setLoading(false);
    }
  };

  const addBrand = (brand) => {
    if (!formData.brands.find(b => b.id === brand.id)) {
      setFormData({
        ...formData,
        brands: [...formData.brands, brand]
      });
    }
    setBrandSearch('');
    setSearchResults([]);
  };

  const removeBrand = (brandId) => {
    setFormData({
      ...formData,
      brands: formData.brands.filter(b => b.id !== brandId)
    });
  };

  const calculateCredits = () => {
    const perCycle = formData.brands.length * formData.itemsPerBrand;
    let cyclesPerMonth = 1;
    if (formData.frequency === 'weekly') cyclesPerMonth = 4;
    return perCycle * cyclesPerMonth;
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(
        `${API}/boutiques`,
        {
          name: formData.name,
          model_album_id: formData.modelAlbumId,
          model_photo_id: formData.modelPhotoId,
          brands: formData.brands,
          items_per_brand: formData.itemsPerBrand,
          frequency: formData.frequency,
          generate_now: formData.generateNow
        },
        {
          headers: { Authorization: `Bearer ${sessionToken}` }
        }
      );

      if (response.data.success) {
        onComplete(response.data.boutique_id);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create boutique');
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return true; // Welcome
      case 2: return formData.name.trim().length > 0;
      case 3: return formData.modelAlbumId.length > 0;
      case 4: return formData.modelPhotoId.length > 0;
      case 5: return formData.brands.length > 0;
      case 6: return true; // Preferences
      case 7: return true; // Review
      default: return false;
    }
  };

  const nextStep = () => {
    if (canProceed() && currentStep < 7) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="wizard-step">
            <div className="step-icon">🎨</div>
            <h2>Create Your Personal Boutique</h2>
            <p className="step-description">
              Let's set up your personalized fashion feed! You'll be the model for every outfit, 
              so you can see exactly how each look works for you.
            </p>
            <div className="features-list">
              <div className="feature-item">✓ Choose your favorite brands</div>
              <div className="feature-item">✓ Auto-generated try-ons with your photo</div>
              <div className="feature-item">✓ Fresh looks on your schedule</div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="wizard-step">
            <div className="step-icon">✏️</div>
            <h2>Name Your Boutique</h2>
            <p className="step-description">Give your boutique a name. You can create multiple boutiques later!</p>
            <input
              type="text"
              className="wizard-input"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., My Style, Wife's Fashion, Kids Outfits"
            />
            <div className="helper-text">
              💡 Examples: "My Style", "Sarah's Wardrobe", "Kids Fashion"
            </div>
          </div>
        );

      case 3:
        return (
          <div className="wizard-step">
            <div className="step-icon">📁</div>
            <h2>Select Model Album</h2>
            <p className="step-description">Choose the album containing photos you want to use as your model.</p>
            <select
              className="wizard-select"
              value={formData.modelAlbumId}
              onChange={(e) => setFormData({ ...formData, modelAlbumId: e.target.value, modelPhotoId: '' })}
            >
              <option value="">-- Select an album --</option>
              {albums.map(album => (
                <option key={album.id} value={album.id}>
                  {album.name} ({album.photo_count} photos)
                </option>
              ))}
            </select>
            {albums.length === 0 && (
              <div className="helper-text">
                ℹ️ No albums found. Please create an album first.
              </div>
            )}
          </div>
        );

      case 4:
        return (
          <div className="wizard-step">
            <div className="step-icon">📸</div>
            <h2>Choose Your Model Photo</h2>
            <p className="step-description">Select the photo that will be used for all try-ons in this boutique.</p>
            <div className="photo-grid">
              {photos.map(photo => (
                <div
                  key={photo.id}
                  className={`photo-item ${formData.modelPhotoId === photo.id ? 'selected' : ''}`}
                  onClick={() => setFormData({ ...formData, modelPhotoId: photo.id })}
                >
                  <img src={photo.image_base64} alt="Model" />
                  {formData.modelPhotoId === photo.id && <div className="selected-badge">✓</div>}
                </div>
              ))}
            </div>
            {photos.length === 0 && (
              <div className="helper-text">
                ℹ️ No photos in this album. Please select a different album.
              </div>
            )}
          </div>
        );

      case 5:
        return (
          <div className="wizard-step">
            <div className="step-icon">🏷️</div>
            <h2>Select Your Favorite Brands</h2>
            <p className="step-description">Search and add brands you love. Mix big brands and boutique merchants!</p>
            
            <div className="brand-search">
              <input
                type="text"
                className="wizard-input"
                value={brandSearch}
                onChange={(e) => setBrandSearch(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && searchBrands()}
                placeholder="Search for brands... (e.g., H&M, Zara, Nike)"
              />
              <button className="btn-search" onClick={searchBrands} disabled={loading}>
                🔍 Search
              </button>
            </div>

            {searchResults.length > 0 && (
              <div className="search-results">
                {searchResults.map(brand => (
                  <div key={brand.id} className="brand-result" onClick={() => addBrand(brand)}>
                    {brand.name} <span className="add-icon">+</span>
                  </div>
                ))}
              </div>
            )}

            <div className="selected-brands">
              <h3>Selected Brands ({formData.brands.length})</h3>
              {formData.brands.map(brand => (
                <div key={brand.id} className="brand-chip">
                  {brand.name}
                  <button className="remove-btn" onClick={() => removeBrand(brand.id)}>×</button>
                </div>
              ))}
              {formData.brands.length === 0 && (
                <p className="helper-text">No brands selected yet. Search and add some!</p>
              )}
            </div>
          </div>
        );

      case 6:
        return (
          <div className="wizard-step">
            <div className="step-icon">⚙️</div>
            <h2>Configure Preferences</h2>
            <p className="step-description">Customize how often you want new looks and how many items per brand.</p>
            
            <div className="preference-group">
              <label>Items per Brand</label>
              <select
                className="wizard-select"
                value={formData.itemsPerBrand}
                onChange={(e) => setFormData({ ...formData, itemsPerBrand: parseInt(e.target.value) })}
              >
                <option value={3}>3 items</option>
                <option value={5}>5 items</option>
                <option value={10}>10 items</option>
                <option value={15}>15 items</option>
              </select>
            </div>

            <div className="preference-group">
              <label>Update Frequency</label>
              <select
                className="wizard-select"
                value={formData.frequency}
                onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
              >
                <option value="weekly">Weekly (Every Monday)</option>
                <option value="monthly">Monthly (1st of each month)</option>
                <option value="on_upload">When brands upload new items</option>
              </select>
            </div>

            <div className="preference-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.generateNow}
                  onChange={(e) => setFormData({ ...formData, generateNow: e.target.checked })}
                />
                {' '}Generate first batch now
              </label>
            </div>
          </div>
        );

      case 7:
        return (
          <div className="wizard-step">
            <div className="step-icon">✅</div>
            <h2>Review & Confirm</h2>
            <p className="step-description">Double-check everything looks good!</p>
            
            <div className="review-section">
              <div className="review-item">
                <strong>Boutique Name:</strong> {formData.name}
              </div>
              <div className="review-item">
                <strong>Brands Selected:</strong> {formData.brands.length}
                <div className="brands-preview">
                  {formData.brands.map(b => b.name).join(', ')}
                </div>
              </div>
              <div className="review-item">
                <strong>Items per Brand:</strong> {formData.itemsPerBrand}
              </div>
              <div className="review-item">
                <strong>Update Frequency:</strong> {formData.frequency === 'weekly' ? 'Weekly' : formData.frequency === 'monthly' ? 'Monthly' : 'On new uploads'}
              </div>
              <div className="review-item">
                <strong>Generate Now:</strong> {formData.generateNow ? 'Yes ✓' : 'No, wait for schedule'}
              </div>
            </div>

            <div className="credits-info">
              <div className="credits-box">
                <h3>💳 Credits Usage</h3>
                <div className="credits-calc">
                  <div>Per Generation: <strong>{formData.brands.length} × {formData.itemsPerBrand} = {formData.brands.length * formData.itemsPerBrand} credits</strong></div>
                  <div>Monthly Total: <strong>~{calculateCredits()} credits</strong></div>
                </div>
                <p className="credits-note">
                  {formData.generateNow && `First batch will use ${formData.brands.length * formData.itemsPerBrand} credits now.`}
                </p>
              </div>
            </div>

            {error && <div className="error-message">{error}</div>}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="wizard-overlay">
      <div className="wizard-container">
        <div className="wizard-header">
          <div className="step-indicator">
            Step {currentStep} of 7
          </div>
          <button className="close-btn" onClick={onCancel}>×</button>
        </div>

        <div className="wizard-progress">
          <div className="progress-bar" style={{ width: `${(currentStep / 7) * 100}%` }} />
        </div>

        <div className="wizard-content">
          {renderStep()}
        </div>

        <div className="wizard-footer">
          {currentStep > 1 && (
            <button className="btn-back" onClick={prevStep} disabled={loading}>
              ← Back
            </button>
          )}
          <div style={{ flex: 1 }} />
          {currentStep < 7 ? (
            <button 
              className="btn-next" 
              onClick={nextStep} 
              disabled={!canProceed()}
            >
              Continue →
            </button>
          ) : (
            <button 
              className="btn-create" 
              onClick={handleSubmit} 
              disabled={loading || !canProceed()}
            >
              {loading ? '⏳ Creating...' : '🎉 Create My Boutique'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BoutiqueSetupWizard;
